/**
 * monitoring.js — Sentry (errores en producción)
 *
 * Inerte hasta configurar VITE_SENTRY_DSN en Vercel (Settings → Environment
 * Variables) con el DSN del proyecto de Sentry. Sin DSN no se inicializa nada
 * y la app funciona exactamente igual.
 *
 * Qué captura: excepciones no manejadas, promesas rechazadas y los
 * `captureMessage` explícitos (p. ej. fallos silenciosos de envío WhatsApp).
 * El tag `organization_id` permite filtrar por negocio afectado.
 */
import * as Sentry from '@sentry/react';

const DSN = import.meta.env.VITE_SENTRY_DSN;

export const initMonitoring = () => {
    if (!DSN || import.meta.env.DEV) return;
    Sentry.init({
        dsn: DSN,
        environment: import.meta.env.MODE,
        // Solo errores (sin tracing) para mantener el plan gratuito holgado.
        tracesSampleRate: 0,
        // Ruido conocido que no es accionable:
        ignoreErrors: [
            'ResizeObserver loop',
            'Network request failed',
            'Failed to fetch',
            'Load failed',
        ],
    });
};

/** Asociar el negocio a los eventos (llamar cuando se conoce la org). */
export const setMonitoringOrg = (organizationId, orgName) => {
    if (!DSN) return;
    Sentry.setTag('organization_id', organizationId || 'unknown');
    if (orgName) Sentry.setTag('organization_name', orgName);
};

/** Aviso explícito de un fallo no-fatal (ej. notificación que no salió). */
export const reportIssue = (message, extra = {}) => {
    if (!DSN) { console.warn('[monitoring]', message, extra); return; }
    Sentry.captureMessage(message, { level: 'warning', extra });
};

export { Sentry };
